import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { App } from 'antd'; // Use App for themed messages
import api from '../api/client';

const GenerationContext = createContext();

export const useGeneration = () => useContext(GenerationContext);

export const GenerationProvider = ({ children }) => {
    const { message } = App.useApp(); // Get themed message instance
    const [generations, setGenerations] = useState([]);
    const pollingIntervalRef = useRef(null);

    // Add a new generation to track
    const trackGeneration = useCallback((id, modelId, metadata = {}, callbacks) => {
        // Handle backward compatibility where callbacks might be just onComplete function
        const onComplete = typeof callbacks === 'function' ? callbacks : callbacks?.onComplete;
        const onFail = callbacks?.onFail;

        setGenerations(prev => [
            {
                id,
                modelId,
                status: 'IN_QUEUE',
                queuePosition: null,
                logs: [],
                metadata: {
                    prompt: 'Generating...',
                    type: 'unknown',
                    startTime: Date.now(),
                    ...metadata
                },
                onComplete, // Callback when finished
                onFail      // Callback when failed
            },
            ...prev
        ]);
    }, []);

    // Poll for status
    const checkStatus = useCallback(async () => {
        // Use a ref-like approach or just the current state since we are inside the interval closure
        // However, generations in the dependency array means this function is recreated when generations changes.

        const activeGens = generations.filter(g => ['IN_QUEUE', 'IN_PROGRESS'].includes(g.status));
        if (activeGens.length === 0) return;

        const updatedGenerations = await Promise.all(activeGens.map(async (gen) => {
            try {
                let statusData;

                // Determine provider based on modelId or metadata
                const isLegnext = gen.modelId === 'midjourney' || gen.modelId === 'legnext' || gen.metadata?.provider === 'legnext';
                const isPinterest = gen.modelId === 'pinterest' || gen.metadata?.provider === 'pinterest';

                if (isPinterest) {
                    // Poll Pinterest Endpoint
                    const response = await api.get('/pinterest/queue/status', {
                        params: {
                            job_id: gen.id
                        }
                    });
                    statusData = response.data;
                } else if (isLegnext) {
                    // Poll Legnext Endpoint
                    const response = await api.get('/legnext/queue/status', {
                        params: {
                            job_id: gen.id
                        }
                    });
                    statusData = response.data;
                } else {
                    // Poll Fal Endpoint (Default)
                    // GET /fal/queue/status?model_id=...&request_id=...
                    const response = await api.get('/fal/queue/status', {
                        params: {
                            model_id: gen.modelId,
                            request_id: gen.id
                        }
                    });
                    statusData = response.data;
                }

                // Check if status changed
                const safeStatus = (statusData.status || 'IN_QUEUE').toUpperCase();
                if (safeStatus !== gen.status || (statusData.logs && statusData.logs.length !== gen.logs?.length)) {
                    // console.log(`Status transition for ${gen.id}: ${gen.status} -> ${safeStatus}`);
                    return { ...gen, ...statusData, status: safeStatus, lastUpdated: Date.now() };
                }
                return gen;
            } catch (error) {
                console.error(`Failed to check status for ${gen.id}:`, error);
                return gen;
            }
        }));

        // Identify changes and trigger side effects BEFORE state update
        // We compare the NEW updatedGenerations with the OLD 'generations' (closure)
        updatedGenerations.forEach(updated => {
            const original = generations.find(g => g.id === updated.id);
            if (!original) return;

            // Transition to COMPLETED
            if (updated.status === 'COMPLETED' && original.status !== 'COMPLETED') {
                if (updated.onComplete) {
                    // Fetch the final result
                    const isLegnext = updated.modelId === 'midjourney' || updated.modelId === 'legnext' || updated.metadata?.provider === 'legnext';
                    const isPinterest = updated.modelId === 'pinterest' || updated.metadata?.provider === 'pinterest';

                    let resultPromise;
                    if (isPinterest) {
                        resultPromise = api.get('/pinterest/queue/result', { params: { job_id: updated.id } });
                    } else if (isLegnext) {
                        resultPromise = api.get('/legnext/queue/result', { params: { job_id: updated.id } });
                    } else {
                        resultPromise = api.get('/fal/queue/result', {
                            params: {
                                model_id: updated.modelId,
                                request_id: updated.id,
                                canvas_id: updated.metadata?.canvas_id,
                                node_id: updated.metadata?.node_id,
                                prompt: updated.metadata?.promptProp || updated.metadata?.prompt,
                                is_panorama: updated.metadata?.is_panorama,
                                width: updated.metadata?.width,
                                height: updated.metadata?.height
                            }
                        });
                    }

                    resultPromise
                        .then(r => updated.onComplete(r.data))
                        .catch(err => {
                            console.error("Failed to fetch final result", err);
                            message.error("Failed to retrieve generation result");
                        });
                }

                // Auto dismiss after 5 seconds
                setTimeout(() => {
                    dismissGeneration(updated.id);
                }, 5000);
            }

            // Transition to FAILED
            if (updated.status === 'FAILED' && original.status !== 'FAILED') {
                if (updated.onFail) {
                    updated.onFail(updated);
                }
                message.error(`Generation failed: ${updated.metadata?.prompt || 'Media'}`);
            }
        });

        // Update state with the new objects
        setGenerations(prev => prev.map(p => {
            const updated = updatedGenerations.find(u => u.id === p.id);
            return updated || p;
        }));

    }, [generations, message]);

    // Polling Effect
    useEffect(() => {
        const hasActive = generations.some(g => ['IN_QUEUE', 'IN_PROGRESS'].includes(g.status));

        if (hasActive && !pollingIntervalRef.current) {
            pollingIntervalRef.current = setInterval(checkStatus, 2000); // Poll every 2s
        } else if (!hasActive && pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [generations, checkStatus]);

    // Clear completed
    const clearCompleted = () => {
        setGenerations(prev => prev.filter(g => ['IN_QUEUE', 'IN_PROGRESS'].includes(g.status)));
    };

    const dismissGeneration = (id) => {
        setGenerations(prev => prev.filter(g => g.id !== id));
    };

    return (
        <GenerationContext.Provider value={{ generations, trackGeneration, clearCompleted, dismissGeneration }}>
            {children}
        </GenerationContext.Provider>
    );
};
